import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';
import './pages/Pages.css';
import Sidebar from './components/Sidebar';
import Home from './pages/Home';
import BooksOverview from './pages/BooksOverview';
import BooksDetails from './pages/BooksDetails';
import BooksPublishedData from './pages/BooksPublishedData';
import BookTags from './pages/BookTags';
import SeriesOverview from './pages/SeriesOverview';
import SeriesDetails from './pages/SeriesDetails';
import SeriesBooksDetails from './pages/SeriesBooksDetails';
import AccountsOverview from './pages/AccountsOverview';
import AccountsDetails from './pages/AccountsDetails';
import EtsAnalytics from './pages/EtsAnalytics';
import EtsAnalyticsDetails from './pages/EtsAnalyticsDetails';
import DownloadedBooks from './pages/DownloadedBooks';
import BookDownloads from './pages/BookDownloads';
import UserDownloads from './pages/UserDownloads';
import UserDownloadedBooks from './pages/UserDownloadedBooks';
import PublishedBooksPerDay from './pages/PublishedBooksPerDay';
import Login from './pages/Login';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function AppLayout({ children }) {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Handle dark mode toggle
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  // Persist sidebar collapsed state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  return (
    <div className="App">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={toggleSidebar} />
      <main className={`main-content ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Routes>
          <Route path="/" element={<Home isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />} />
          <Route path="/books/overview" element={<BooksOverview isDarkMode={isDarkMode} />} />
          <Route path="/books/data" element={<BooksDetails />} />
          <Route path="/books/published-data" element={<BooksPublishedData />} />
          <Route path="/series/overview" element={<SeriesOverview isDarkMode={isDarkMode} />} />
          <Route path="/series/details" element={<SeriesDetails />} />
          <Route path="/series/:seriesId/books" element={<SeriesBooksDetails />} />
          <Route path="/accounts/overview" element={<AccountsOverview isDarkMode={isDarkMode} />} />
          <Route path="/accounts/details" element={<AccountsDetails />} />
          <Route path="/ets-analytics/overview" element={<EtsAnalytics isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />} />
          <Route path="/ets-analytics/details" element={<EtsAnalyticsDetails />} />
          <Route path="/books/downloads-per-day" element={<DownloadedBooks />} />
          <Route path="/books/book-downloads" element={<BookDownloads />} />
          <Route path="/downloads/user-downloads" element={<UserDownloads />} />
          <Route path="/downloads/user/:userId/books" element={<UserDownloadedBooks />} />
          <Route path="/books/published-per-day" element={<PublishedBooksPerDay />} />
          <Route path="/books/tags" element={<BookTags />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/*" element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            } />
          </Routes>
        </AuthProvider>
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
